import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { Subject, takeUntil, firstValueFrom } from 'rxjs';
import { MarketingService } from '../../services/marketing.service';
import { AuthService } from '../../services/auth.service';
import { ListingsService } from '../../services/listings.service';
import { PlanConfigService } from '../../services/plan.config.service';

interface MarketingStats {
  total_subscribers: number;
  active_subscribers: number;
  unsubscribed: number;
  total_campaigns: number;
  total_emails_sent: number;
  avg_recipients_per_campaign: number;
  last_campaign_sent: string | null;
}

interface Campaign {
  id: string;
  title: string;
  subject: string;
  content: string;
  recipients_count: number;
  sent_at: string;
  created_at: string;
  sent_by_name: string;
  sent_by_email: string;
}

interface Subscriber {
  id: number;
  email: string;
  user_id: number;
  first_name: string;
  last_name: string;
  created_at: string;
}

interface CampaignPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface CampaignHistoryResponse {
  status: string;
  data: {
    campaigns: Campaign[];
    total: number;
    limit: number;
    offset: number;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface SubscribersResponse {
  status: string;
  data: {
    subscribers: Subscriber[];
    total: number;
  };
}

interface SendCampaignResponse {
  status: string;
  message: string;
  data: {
    campaign: Campaign;
    stats: {
      totalSubscribers: number;
      successfulSends: number;
      failedSends: number;
    };
  };
}

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  is_admin?: boolean;
}

interface EliteListing {
  id: number;
  title: string;
  price: number;
  currency: string;
  category: string;
  subcategory: string;
  table_name: string;
  status: string;
  created_at: string;
  images: string[];
  first_image: string | null;
  user_email: string;
  user_first_name: string;
  user_last_name: string;
  plan_name: string;
  selected?: boolean;
}

@Component({
  selector: 'app-admin-marketing',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, ReactiveFormsModule],
  templateUrl: './admin-marketing.component.html',
  styleUrl: './admin-marketing.component.css',
})
export class AdminMarketingComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  loading = true;
  error: string | null = null;
  currentAdmin: User | null = null;
  activeTab: 'campaigns' | 'subscribers' = 'campaigns';

  stats: MarketingStats | null = null;
  campaigns: Campaign[] = [];
  subscribers: Subscriber[] = [];
  filteredSubscribers: Subscriber[] = [];

  loadingCampaigns = false;
  loadingSubscribers = false;
  subscriberSearchTerm = '';

  showSendCampaignModal = false;
  campaignForm!: FormGroup;
  sendingCampaign = false;
  campaignError = '';
  campaignSuccess = '';

  showListingMarketingModal = false;
  eliteListings: EliteListing[] = [];
  loadingEliteListings = false;
  eliteListingsError = '';
  selectedListings: EliteListing[] = [];
  sendingListingCampaign = false;
  listingCampaignError = '';
  listingCampaignSuccess = '';
  listingSearchTerm = '';
  filteredEliteListings: EliteListing[] = [];
  listingCampaignSubject = '';
  listingCampaignIntro = '';

  showCampaignDetailsModal = false;
  selectedCampaign: Campaign | null = null;
  loadingCampaignDetails = false;
  campaignDetailsError: string | null = null;

  campaignPagination: CampaignPagination = {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  };

  constructor(
    private marketingService: MarketingService,
    private authService: AuthService,
    private listingsService: ListingsService,
    private planConfigService: PlanConfigService,
    private router: Router,
    private formBuilder: FormBuilder
  ) {
    this.initializeCampaignForm();
  }

  ngOnInit(): void {
    this.initializeData();
    this.loadCurrentAdmin();
    this.loadMarketingData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeData(): void {
    this.campaigns = [];
    this.subscribers = [];
    this.filteredSubscribers = [];
    this.eliteListings = [];
    this.filteredEliteListings = [];
  }

  private initializeCampaignForm(): void {
    this.campaignForm = this.formBuilder.group({
      title: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(255),
        ],
      ],
      subject: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(255),
        ],
      ],
      content: ['', [Validators.required, Validators.minLength(10)]],
    });
  }

  private loadCurrentAdmin(): void {
    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: User | null) => {
        this.currentAdmin = user;
        if (!user?.is_admin) {
          this.router.navigate(['/home']);
        }
      });
  }

  private async loadMarketingData(): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      await Promise.all([
        this.loadMarketingStats(),
        this.loadCampaigns(),
        this.loadSubscribers(),
      ]);
    } catch (error) {
      console.error('Error loading marketing data:', error);
      this.error = 'Failed to load marketing data. Please try again.';

      if (!this.campaigns) this.campaigns = [];
      if (!this.subscribers) this.subscribers = [];
      if (!this.filteredSubscribers) this.filteredSubscribers = [];
    } finally {
      this.loading = false;
    }
  }

  private async loadMarketingStats(): Promise<void> {
    try {
      this.stats = await firstValueFrom(
        this.marketingService.getMarketingStats()
      );
    } catch (error) {
      console.error('Error loading marketing stats:', error);
      throw error;
    }
  }

  async loadCampaigns(page: number = 1): Promise<void> {
    this.loadingCampaigns = true;
    try {
      const response: CampaignHistoryResponse = await firstValueFrom(
        this.marketingService.getCampaignHistory(
          {},
          page,
          this.campaignPagination.limit
        )
      );

      this.campaigns = response.data.campaigns || [];

      this.campaignPagination = {
        page: response.pagination.page || 1,
        limit: response.pagination.limit || 10,
        total: response.pagination.total || 0,
        totalPages: response.pagination.totalPages || 1,
      };
    } catch (error) {
      console.error('Error loading campaigns:', error);
      this.campaigns = [];
      this.campaignPagination = {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1,
      };
      throw error;
    } finally {
      this.loadingCampaigns = false;
    }
  }

  onCampaignPageChange(page: number): void {
    if (page >= 1 && page <= this.campaignPagination.totalPages && page !== this.campaignPagination.page) {
      this.loadCampaigns(page);
    }
  }

  getCampaignPageNumbers(): number[] {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, this.campaignPagination.page - Math.floor(maxVisible / 2));
    let end = Math.min(this.campaignPagination.totalPages, start + maxVisible - 1);

    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }

  shouldShowCampaignEllipsis(position: 'start' | 'end'): boolean {
    const maxVisible = 5;
    const halfVisible = Math.floor(maxVisible / 2);

    if (position === 'start') {
      return this.campaignPagination.page > halfVisible + 1 && this.campaignPagination.totalPages > maxVisible;
    } else {
      return this.campaignPagination.page < this.campaignPagination.totalPages - halfVisible && this.campaignPagination.totalPages > maxVisible;
    }
  }

  private async loadSubscribers(): Promise<void> {
    this.loadingSubscribers = true;
    try {
      const response: SubscribersResponse = await firstValueFrom(
        this.marketingService.getSubscribers()
      );
      this.subscribers = response.data.subscribers || [];
      this.filteredSubscribers = [...this.subscribers];
    } catch (error) {
      console.error('Error loading subscribers:', error);
      this.subscribers = [];
      this.filteredSubscribers = [];
      throw error;
    } finally {
      this.loadingSubscribers = false;
    }
  }

  async loadEliteListings(): Promise<void> {
    this.loadingEliteListings = true;
    this.eliteListingsError = '';

    try {
      const response = await firstValueFrom(
        this.marketingService.getEliteListings()
      );

      this.eliteListings = (response.data.listings || []).map((listing: any) => ({
        ...listing,
        selected: false,
      }));

      this.filteredEliteListings = [...this.eliteListings];
    } catch (error: any) {
      console.error('Error loading elite listings:', error);
      this.eliteListingsError =
        error.message || 'Failed to load Elite listings. Please try again.';
      this.eliteListings = [];
      this.filteredEliteListings = [];
    } finally {
      this.loadingEliteListings = false;
    }
  }

  goBack(): void {
    this.router.navigate(['/admin/dashboard']);
  }

  onRefresh(): void {
    this.campaignPagination.page = 1;
    this.loadMarketingData();
  }

  setActiveTab(tab: 'campaigns' | 'subscribers'): void {
    this.activeTab = tab;
  }

  openSendCampaignModal(): void {
    this.showSendCampaignModal = true;
    this.campaignForm.reset();
    this.campaignError = '';
    this.campaignSuccess = '';
  }

  closeSendCampaignModal(): void {
    this.showSendCampaignModal = false;
    this.campaignForm.reset();
    this.campaignError = '';
    this.campaignSuccess = '';
    this.sendingCampaign = false;
  }

  async sendCampaign(): Promise<void> {
    if (this.campaignForm.invalid) {
      this.markFormGroupTouched(this.campaignForm);
      return;
    }

    this.sendingCampaign = true;
    this.campaignError = '';
    this.campaignSuccess = '';

    try {
      const campaignData = this.campaignForm.value;
      const response: SendCampaignResponse = await firstValueFrom(
        this.marketingService.sendCampaign(campaignData)
      );

      this.campaignSuccess = `Campaign sent successfully to ${response.data.stats.successfulSends} subscribers!`;

      await Promise.all([this.loadMarketingStats(), this.loadCampaigns(1)]);
      this.campaignPagination.page = 1;

      setTimeout(() => {
        this.closeSendCampaignModal();
      }, 2000);
    } catch (error: any) {
      console.error('Error sending campaign:', error);
      this.campaignError =
        error.message || 'Failed to send campaign. Please try again.';
    } finally {
      this.sendingCampaign = false;
    }
  }

  async viewCampaign(campaign: Campaign): Promise<void> {
    this.selectedCampaign = campaign;
    this.showCampaignDetailsModal = true;
    this.loadingCampaignDetails = true;
    this.campaignDetailsError = null;

    try {
      const fullCampaign = await firstValueFrom(
        this.marketingService.getCampaignById(campaign.id)
      );

      this.selectedCampaign = fullCampaign;
    } catch (error) {
      console.error('Error loading campaign details:', error);
      this.campaignDetailsError =
        'Failed to load campaign content. Please try again.';
    } finally {
      this.loadingCampaignDetails = false;
    }
  }

  closeCampaignDetailsModal(): void {
    this.showCampaignDetailsModal = false;
    this.selectedCampaign = null;
    this.loadingCampaignDetails = false;
    this.campaignDetailsError = null;
  }

  openListingMarketingModal(): void {
    this.showListingMarketingModal = true;
    this.resetListingModalState();
    this.loadEliteListings();
  }

  closeListingMarketingModal(): void {
    this.showListingMarketingModal = false;
    this.resetListingModalState();
  }

  private resetListingModalState(): void {
    this.selectedListings = [];
    this.eliteListingsError = '';
    this.listingCampaignError = '';
    this.listingCampaignSuccess = '';
    this.listingSearchTerm = '';
    this.listingCampaignSubject = 'Check out these premium listings!';
    this.listingCampaignIntro = '';
    this.sendingListingCampaign = false;
    this.eliteListings = [];
    this.filteredEliteListings = [];
  }

  isListingSelected(listingId: number): boolean {
    return this.selectedListings.some((listing) => listing.id === listingId);
  }

  toggleListingSelection(listing: EliteListing): void {
    const isSelected = this.isListingSelected(listing.id);

    if (isSelected) {
      this.selectedListings = this.selectedListings.filter(
        (l) => l.id !== listing.id
      );
    } else {
      this.selectedListings.push(listing);
    }

    this.updateListingSelectionState(listing.id, !isSelected);
  }

  private updateListingSelectionState(
    listingId: number,
    selected: boolean
  ): void {
    const eliteListingIndex = this.eliteListings.findIndex(
      (l) => l.id === listingId
    );
    if (eliteListingIndex !== -1) {
      this.eliteListings[eliteListingIndex].selected = selected;
    }

    const filteredListingIndex = this.filteredEliteListings.findIndex(
      (l) => l.id === listingId
    );
    if (filteredListingIndex !== -1) {
      this.filteredEliteListings[filteredListingIndex].selected = selected;
    }
  }

  selectAllListings(): void {
    const allSelected = this.filteredEliteListings.every((l) =>
      this.isListingSelected(l.id)
    );

    if (allSelected) {
      this.filteredEliteListings.forEach((listing) => {
        this.selectedListings = this.selectedListings.filter(
          (l) => l.id !== listing.id
        );
        this.updateListingSelectionState(listing.id, false);
      });
    } else {
      this.filteredEliteListings.forEach((listing) => {
        if (!this.isListingSelected(listing.id)) {
          this.selectedListings.push(listing);
          this.updateListingSelectionState(listing.id, true);
        }
      });
    }
  }

  clearAllSelections(): void {
    this.selectedListings = [];
    this.eliteListings.forEach((listing) => (listing.selected = false));
    this.filteredEliteListings.forEach((listing) => (listing.selected = false));
  }

  filterEliteListings(): void {
    if (!this.listingSearchTerm.trim()) {
      this.filteredEliteListings = [...this.eliteListings];
      return;
    }

    const searchTerm = this.listingSearchTerm.toLowerCase();
    this.filteredEliteListings = this.eliteListings.filter(
      (listing) =>
        listing.title.toLowerCase().includes(searchTerm) ||
        listing.category.toLowerCase().includes(searchTerm) ||
        listing.subcategory.toLowerCase().includes(searchTerm)
    );
  }

  async sendListingMarketingCampaign(): Promise<void> {
    if (this.selectedListings.length === 0) {
      this.listingCampaignError =
        'Please select at least one listing to feature';
      return;
    }

    this.sendingListingCampaign = true;
    this.listingCampaignError = '';
    this.listingCampaignSuccess = '';

    try {
      const listingIds = this.selectedListings.map((l) => l.id);

      const response = await firstValueFrom(
        this.marketingService.sendListingCampaign({
          listingIds,
          subject:
            this.listingCampaignSubject || 'Check out these premium listings!',
          intro:
            this.listingCampaignIntro ||
            'Discover our handpicked premium listings from our marketplace!',
        })
      );

      this.listingCampaignSuccess = `Elite listing campaign sent successfully to ${response.data.stats.successfulSends} subscribers!`;

      await Promise.all([this.loadMarketingStats(), this.loadCampaigns(1)]);
      this.campaignPagination.page = 1;

      setTimeout(() => {
        this.closeListingMarketingModal();
      }, 2000);
    } catch (error: any) {
      console.error('Error sending elite listing campaign:', error);
      this.listingCampaignError =
        error.message ||
        'Failed to send elite listing campaign. Please try again.';
    } finally {
      this.sendingListingCampaign = false;
    }
  }

  filterSubscribers(): void {
    if (!this.subscriberSearchTerm.trim()) {
      this.filteredSubscribers = [...this.subscribers];
      return;
    }

    const searchTerm = this.subscriberSearchTerm.toLowerCase();
    this.filteredSubscribers = this.subscribers.filter(
      (subscriber) =>
        subscriber.first_name.toLowerCase().includes(searchTerm) ||
        subscriber.last_name.toLowerCase().includes(searchTerm) ||
        subscriber.email.toLowerCase().includes(searchTerm)
    );
  }

  getInitials(firstName: string, lastName: string): string {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'Never';

    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - date.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return 'Today';
      } else if (diffDays === 1) {
        return 'Yesterday';
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
      } else {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      }
    } catch (error) {
      return 'Unknown date';
    }
  }

  formatPrice(price: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(price);
  }

  formatCategory(category: string): string {
    return (
      category.charAt(0).toUpperCase() + category.slice(1).replace(/-/g, ' ')
    );
  }

  getCategoryDisplay(tableName: string): string {
    const categoryMap: { [key: string]: string } = {
      real_estate: 'Real Estate',
      vehicles: 'Vehicles',
      electronics: 'Electronics',
      jobs: 'Jobs',
      services: 'Services',
      marketplace: 'Marketplace',
      housing: 'Housing',
      for_sale: 'For Sale',
      wanted: 'Wanted',
      gigs: 'Gigs',
      community: 'Community',
      events: 'Events',
      discussion: 'Discussion',
    };

    return categoryMap[tableName] || this.formatCategory(tableName);
  }

  getTimeSinceLastCampaign(): string {
    if (!this.stats?.last_campaign_sent) {
      return 'Never';
    }

    try {
      const lastCampaign = new Date(this.stats.last_campaign_sent);
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - lastCampaign.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return 'Today';
      } else if (diffDays === 1) {
        return '1 day';
      } else if (diffDays < 7) {
        return `${diffDays} days`;
      } else if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return `${weeks} week${weeks > 1 ? 's' : ''}`;
      } else if (diffDays < 365) {
        const months = Math.floor(diffDays / 30);
        return `${months} month${months > 1 ? 's' : ''}`;
      } else {
        const years = Math.floor(diffDays / 365);
        return `${years} year${years > 1 ? 's' : ''}`;
      }
    } catch (error) {
      return 'Unknown';
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      control?.markAsTouched({ onlySelf: true });
    });
  }
}
